import { Reveal } from "@/components/Reveal";
import { SectionLabel } from "@/components/bits";
import LeadDialog from "@/components/LeadDialog";
import { IMAGES } from "@/lib/site";
import { Check } from "lucide-react";

const BUILDER_POINTS = [
  "Plan review and low-voltage design coordinated with your drawings",
  "Clean, documented pre-wire that keeps your schedule on track",
  "Trade coordination with electrical, framing, and finish carpentry",
  "Future-ready infrastructure that adds value at sale",
];
const DESIGNER_POINTS = [
  "Concealed hardware that protects your finishes and sightlines",
  "Keypads, shades, and fixtures selected to match your palette",
  "Mock-ups and samples for client presentations",
  "A single technology partner across the entire residence",
];

export default function BuildersDesigners() {
  return (
    <div data-testid="page-builders-designers" className="pt-32">
      <header className="mx-auto max-w-[1600px] px-6 pb-16 md:px-12">
        <Reveal>
          <SectionLabel>Builders &amp; Designers</SectionLabel>
          <h1 className="mt-8 max-w-5xl font-serif text-5xl font-300 leading-[0.98] md:text-8xl">
            A trade partner you can <span className="italic accent-text">build with</span>.
          </h1>
          <p className="mt-8 max-w-xl text-base leading-relaxed text-white/60 md:text-lg">
            We integrate seamlessly into your process — reading plans, hitting milestones, and protecting the vision from pre-wire through final calibration.
          </p>
        </Reveal>
      </header>

      <section className="mx-auto grid max-w-[1600px] grid-cols-1 gap-6 px-6 pb-24 md:px-12 lg:grid-cols-2">
        {[
          { title: "For Custom-Home Builders", img: "mansionDusk", points: BUILDER_POINTS },
          { title: "For Interior Designers", img: "poolRoom", points: DESIGNER_POINTS },
        ].map((b, i) => (
          <Reveal key={b.title} delay={i * 0.1} className="border border-white/10 bg-isav-bg2">
            <div className="hover-media relative aspect-[16/9] overflow-hidden">
              <img src={IMAGES[b.img]} alt={b.title} className="h-full w-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-isav-bg2 to-transparent" />
            </div>
            <div className="p-10">
              <h2 className="font-serif text-3xl font-400">{b.title}</h2>
              <ul className="mt-6 space-y-3">
                {b.points.map((p) => (
                  <li key={p} className="flex items-start gap-3 text-sm text-white/70">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-isav-green" /> {p}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        ))}
      </section>

      <section className="border-t border-white/10 bg-isav-bg py-28" data-testid="plan-submission">
        <div className="mx-auto grid max-w-[1600px] grid-cols-1 gap-14 px-6 md:px-12 lg:grid-cols-12 lg:gap-20">
          <Reveal className="lg:col-span-6">
            <SectionLabel>Submit plans &amp; specs</SectionLabel>
            <h2 className="mt-8 font-serif text-4xl font-300 leading-tight md:text-6xl">
              Send us your project. We'll design the low-voltage scope.
            </h2>
            <p className="mt-8 max-w-md text-sm leading-relaxed text-white/60 md:text-base">
              Share a link to your plans, elevations, or spec set. Our team will review and follow up to coordinate a tailored integration design for your project.
            </p>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-6">
            <div className="border border-white/10 bg-isav-bg2 p-10">
              <p className="font-serif text-2xl font-400">Start a project</p>
              <p className="mt-2 text-sm text-white/50">Builders and designers — begin here.</p>
              <div className="mt-8 flex flex-col gap-4">
                <LeadDialog kind="plan" variant="accent" defaultAudience="builder" triggerLabel="Submit Plans" triggerClassName="w-full" testId="submit-plans" />
                <LeadDialog kind="quote" variant="secondary" triggerLabel="Request a Project Quote" triggerClassName="w-full" testId="builders-request-quote" />
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
